import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  Firestore
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import UserIcon from '../authentication/UserIcon';
import '../../styles/DeveloperControlPanel.css';

// Interfaces
interface Permissions {
  read: string | string[];
  write: string | string[];
}

interface MetadataCollection {
  permissions: Permissions;
  fields: MetadataField[];
}

interface MetadataField {
  id: string;
  name: string;
}

// Control Panel Component
const DeveloperControlPanel: React.FC<{ db: Firestore, functions: Functions }> = ({ db, functions }) => {
  const { currentUser } = useAuth();
  const [collections, setCollections] = useState<string[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [metadataCollection, setMetadataCollection] = useState<MetadataCollection | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // New collection form state
  const [newCollectionName, setNewCollectionName] = useState<string>('');
  const [newCollectionReadPermission, setNewCollectionReadPermission] = useState<string>('ALLOW_ALL');
  const [newCollectionReadPermissions, setNewCollectionReadPermissions] = useState<string[]>([]);
  const [newCollectionWritePermission, setNewCollectionWritePermission] = useState<string>('');
  const [newCollectionWritePermissions, setNewCollectionWritePermissions] = useState<string[]>(['APP_ADMIN', 'DEVELOPER']);
  
  // Custom permission input field state (separate from permission type selection)
  const [newCustomReadPermission, setNewCustomReadPermission] = useState<string>('');
  const [newCustomWritePermission, setNewCustomWritePermission] = useState<string>('');
  
  // Edit permissions state
  const [editCollectionReadPermission, setEditCollectionReadPermission] = useState<string>('ALLOW_ALL');
  const [editCollectionReadPermissions, setEditCollectionReadPermissions] = useState<string[]>([]);
  const [editCollectionWritePermission, setEditCollectionWritePermission] = useState<string>('');
  const [editCollectionWritePermissions, setEditCollectionWritePermissions] = useState<string[]>(['APP_ADMIN', 'DEVELOPER']);
  
  // Custom permission input field state for edit mode
  const [editCustomReadPermission, setEditCustomReadPermission] = useState<string>('');
  const [editCustomWritePermission, setEditCustomWritePermission] = useState<string>('');

  // Cloud function references
  const createCollectionFunc = httpsCallable(functions, 'createCollection');
  const setCollectionPermissionsFunc = httpsCallable(functions, 'setCollectionPermissions');

  // Load collections from metadata
  useEffect(() => {
    const loadCollections = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      setError('');
      try {
        const metadataRef = collection(db, 'metadata');
        const metadataSnapshot = await getDocs(metadataRef);
        const collectionNames: string[] = [];
        
        metadataSnapshot.forEach((doc) => {
          collectionNames.push(doc.id);
        });
        
        setCollections(collectionNames);
      } catch (err) {
        console.error('Error loading collections:', err);
        setError('Failed to load collections. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadCollections();
  }, [db, currentUser]);

  // Load collection metadata when selected
  useEffect(() => {
    const loadCollectionMetadata = async () => {
      if (!selectedCollection) {
        setMetadataCollection(null);
        return;
      }
      
      setLoading(true);
      setError('');
      try {
        const metadataRef = doc(db, 'metadata', selectedCollection);
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const data = metadataSnapshot.data() as MetadataCollection;
          setMetadataCollection(data);
          
          // Set edit permissions state
          if (typeof data.permissions.read === 'string') {
            setEditCollectionReadPermission(data.permissions.read);
            setEditCollectionReadPermissions([]);
          } else {
            setEditCollectionReadPermission('CUSTOM');
            setEditCollectionReadPermissions(data.permissions.read);
          }
          
          if (typeof data.permissions.write === 'string') {
            setEditCollectionWritePermission(data.permissions.write);
            setEditCollectionWritePermissions([]);
          } else {
            setEditCollectionWritePermission('CUSTOM');
            setEditCollectionWritePermissions(data.permissions.write);
          }
        } else {
          setMetadataCollection(null);
        }
      } catch (err) {
        console.error('Error loading collection metadata:', err);
        setError('Failed to load collection metadata. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadCollectionMetadata();
  }, [db, selectedCollection]);

  // Create a new collection
  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Prepare permissions object
      let readPermValue: string | string[];
      if (newCollectionReadPermission === 'ALLOW_ALL' || newCollectionReadPermission === 'ALLOW_NONE') {
        readPermValue = newCollectionReadPermission;
      } else if (newCollectionReadPermission === 'CUSTOM') {
        readPermValue = newCollectionReadPermissions;
      } else {
        readPermValue = 'ALLOW_ALL';
      }
      
      let writePermValue: string | string[];
      if (newCollectionWritePermission === 'ALLOW_ALL' || newCollectionWritePermission === 'ALLOW_NONE') {
        writePermValue = newCollectionWritePermission;
      } else if (newCollectionWritePermission === 'CUSTOM') {
        writePermValue = newCollectionWritePermissions;
      } else {
        writePermValue = ['APP_ADMIN', 'DEVELOPER'];
      }
      
      const permissions = {
        read: readPermValue,
        write: writePermValue
      };
      
      const result = await createCollectionFunc({
        name: newCollectionName,
        permissions
      });
      
      // @ts-ignore (result data structure)
      if (result.data.success) {
        setSuccess(`Collection "${newCollectionName}" created successfully.`);
        setCollections([...collections, newCollectionName]);
        setNewCollectionName('');
        setNewCollectionReadPermission('ALLOW_ALL');
        setNewCollectionReadPermissions([]);
        setNewCollectionWritePermission('');
        setNewCollectionWritePermissions(['APP_ADMIN', 'DEVELOPER']);
        setNewCustomReadPermission('');
        setNewCustomWritePermission('');
      } else {
        // @ts-ignore
        setError(result.data.message || 'Failed to create collection.');
      }
    } catch (err: any) {
      console.error('Error creating collection:', err);
      setError(err.message || 'Failed to create collection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update collection permissions
  const handleUpdateCollectionPermissions = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCollection) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Prepare permissions object
      let readPermValue: string | string[];
      if (editCollectionReadPermission === 'ALLOW_ALL' || editCollectionReadPermission === 'ALLOW_NONE') {
        readPermValue = editCollectionReadPermission;
      } else if (editCollectionReadPermission === 'CUSTOM') {
        readPermValue = editCollectionReadPermissions;
      } else {
        readPermValue = 'ALLOW_ALL';
      }
      
      let writePermValue: string | string[];
      if (editCollectionWritePermission === 'ALLOW_ALL' || editCollectionWritePermission === 'ALLOW_NONE') {
        writePermValue = editCollectionWritePermission;
      } else if (editCollectionWritePermission === 'CUSTOM') {
        writePermValue = editCollectionWritePermissions;
      } else {
        writePermValue = ['APP_ADMIN', 'DEVELOPER'];
      }
      
      const permissions = {
        read: readPermValue,
        write: writePermValue
      };
      
      const result = await setCollectionPermissionsFunc({
        name: selectedCollection,
        permissions
      });
      
      // @ts-ignore (result data structure)
      if (result.data.success) {
        setSuccess(`Permissions for collection "${selectedCollection}" updated successfully.`);
        // Refresh collection metadata
        const metadataRef = doc(db, 'metadata', selectedCollection);
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          setMetadataCollection(metadataSnapshot.data() as MetadataCollection);
        }
      } else {
        // @ts-ignore
        setError(result.data.message || 'Failed to update collection permissions.');
      }
    } catch (err: any) {
      console.error('Error updating collection permissions:', err);
      setError(err.message || 'Failed to update permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add and remove permissions from arrays
  const handleAddReadPermission = () => {
    const permission = newCustomReadPermission.trim();
    if (permission && !newCollectionReadPermissions.includes(permission)) {
      setNewCollectionReadPermissions([...newCollectionReadPermissions, permission]);
      setNewCustomReadPermission(''); // Clear the input after adding
    }
  };

  const handleRemoveReadPermission = (permission: string) => {
    setNewCollectionReadPermissions(newCollectionReadPermissions.filter(p => p !== permission));
  };

  const handleAddWritePermission = () => {
    const permission = newCustomWritePermission.trim();
    if (permission && !newCollectionWritePermissions.includes(permission)) {
      setNewCollectionWritePermissions([...newCollectionWritePermissions, permission]);
      setNewCustomWritePermission(''); // Clear the input after adding
    }
  };

  const handleRemoveWritePermission = (permission: string) => {
    setNewCollectionWritePermissions(newCollectionWritePermissions.filter(p => p !== permission));
  };

  // Edit mode
  const handleAddEditReadPermission = () => {
    const permission = editCustomReadPermission.trim();
    if (permission && !editCollectionReadPermissions.includes(permission)) {
      setEditCollectionReadPermissions([...editCollectionReadPermissions, permission]);
      setEditCustomReadPermission(''); // Clear the input after adding
    }
  };

  const handleRemoveEditReadPermission = (permission: string) => {
    setEditCollectionReadPermissions(editCollectionReadPermissions.filter(p => p !== permission));
  };

  const handleAddEditWritePermission = () => {
    const permission = editCustomWritePermission.trim();
    if (permission && !editCollectionWritePermissions.includes(permission)) {
      setEditCollectionWritePermissions([...editCollectionWritePermissions, permission]);
      setEditCustomWritePermission(''); // Clear the input after adding
    }
  };

  const handleRemoveEditWritePermission = (permission: string) => {
    setEditCollectionWritePermissions(editCollectionWritePermissions.filter(p => p !== permission));
  };

  // Render permission badges
  const renderPermissionBadges = (permissions: string | string[]) => {
    if (typeof permissions === 'string') {
      return <p>{permissions}</p>;
    }
    
    return (
      <div className="permissions-badges">
        {permissions.map((perm) => (
          <span key={perm} className="permission-badge">
            {perm}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="developer-panel">
      <header className="panel-header">
        <div className="header-title">
          <h1>Developer Control Panel</h1>
        </div>
        <div className="header-user">
          <UserIcon />
        </div>
      </header>

      {error && (
        <div className="error-alert">
          {error}
        </div>
      )}

      {success && (
        <div className="success-alert">
          {success}
        </div>
      )}

      <div className="panel-content">
        {/* Collections Management Section */}
        <div className="panel-section">
          <h2 className="section-title">Collections</h2>
          
          {/* Create Collection Form */}
          <form onSubmit={handleCreateCollection} className="form-container">
            <h3 className="form-title">Create New Collection</h3>
            
            <div className="form-group">
              <label>Collection Name</label>
              <input
                type="text"
                className="form-input"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Read Permissions</label>
              <select
                className="form-select"
                value={newCollectionReadPermission}
                onChange={(e) => setNewCollectionReadPermission(e.target.value)}
              >
                <option value="ALLOW_ALL">Allow All</option>
                <option value="ALLOW_NONE">Allow None</option>
                <option value="CUSTOM">Custom</option>
              </select>
              
              {newCollectionReadPermission === 'CUSTOM' && (
                <div className="permissions-container">
                  <div className="permissions-input">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Permission name"
                      value={newCustomReadPermission}
                      onChange={(e) => setNewCustomReadPermission(e.target.value)}
                    />
                    <button
                      type="button"
                      className="add-button"
                      onClick={handleAddReadPermission}
                    >
                      Add
                    </button>
                  </div>
                  
                  <div className="permissions-badges">
                    {newCollectionReadPermissions.map((perm) => (
                      <div key={perm} className="permission-badge">
                        {perm}
                        <button
                          type="button"
                          className="remove-permission"
                          onClick={() => handleRemoveReadPermission(perm)}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Write Permissions</label>
              <select
                className="form-select"
                value={newCollectionWritePermission}
                onChange={(e) => setNewCollectionWritePermission(e.target.value)}
              >
                <option value="">Default (APP_ADMIN, DEVELOPER)</option>
                <option value="ALLOW_ALL">Allow All</option>
                <option value="ALLOW_NONE">Allow None</option>
                <option value="CUSTOM">Custom</option>
              </select>
              
              {newCollectionWritePermission === 'CUSTOM' && (
                <div className="permissions-container">
                  <div className="permissions-input">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Permission name"
                      value={newCustomWritePermission}
                      onChange={(e) => setNewCustomWritePermission(e.target.value)}
                    />
                    <button
                      type="button"
                      className="add-button"
                      onClick={handleAddWritePermission}
                    >
                      Add
                    </button>
                  </div>
                  
                  <div className="permissions-badges">
                    {newCollectionWritePermissions.map((perm) => (
                      <div key={perm} className="permission-badge">
                        {perm}
                        <button
                          type="button"
                          className="remove-permission"
                          onClick={() => handleRemoveWritePermission(perm)}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <button
              type="submit"
              className="primary-button"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Collection'}
            </button>
          </form>
          
          {/* Collection List */}
          <div className="collection-list">
            <h3 className="list-title">Available Collections</h3>
            {collections.length > 0 ? (
              <ul className="list-container">
                {collections.map((col) => (
                  <li
                    key={col}
                    className={`list-item ${selectedCollection === col ? 'selected-item' : ''}`}
                    onClick={() => setSelectedCollection(col)}
                  >
                    {col}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-message">No collections available.</p>
            )}
          </div>
        </div>

        {/* Collection Details Section */}
        <div className="panel-section">
          {selectedCollection ? (
            <>
              <div className="section-header">
                <h2 className="section-title">{selectedCollection} Collection</h2>
              </div>
              
              {metadataCollection && (
                <>
                  {/* Edit Collection Permissions */}
                  <div className="form-container">
                    <h3 className="form-title">Edit Collection Permissions</h3>
                    <form onSubmit={handleUpdateCollectionPermissions}>
                      <div className="form-group">
                        <label>Read Permissions</label>
                        <select
                          className="form-select"
                          value={editCollectionReadPermission}
                          onChange={(e) => setEditCollectionReadPermission(e.target.value)}
                        >
                          <option value="ALLOW_ALL">Allow All</option>
                          <option value="ALLOW_NONE">Allow None</option>
                          <option value="CUSTOM">Custom</option>
                        </select>
                        
                        {editCollectionReadPermission === 'CUSTOM' && (
                          <div className="permissions-container">
                            <div className="permissions-input">
                              <input
                                type="text"
                                className="form-input"
                                placeholder="Permission name"
                                value={editCustomReadPermission}
                                onChange={(e) => setEditCustomReadPermission(e.target.value)}
                              />
                              <button
                                type="button"
                                className="add-button"
                                onClick={handleAddEditReadPermission}
                              >
                                Add
                              </button>
                            </div>
                            
                            <div className="permissions-badges">
                              {editCollectionReadPermissions.map((perm) => (
                                <div key={perm} className="permission-badge">
                                  {perm}
                                  <button
                                    type="button"
                                    className="remove-permission"
                                    onClick={() => handleRemoveEditReadPermission(perm)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label>Write Permissions</label>
                        <select
                          className="form-select"
                          value={editCollectionWritePermission}
                          onChange={(e) => setEditCollectionWritePermission(e.target.value)}
                        >
                          <option value="ALLOW_ALL">Allow All</option>
                          <option value="ALLOW_NONE">Allow None</option>
                          <option value="CUSTOM">Custom</option>
                        </select>
                        
                        {editCollectionWritePermission === 'CUSTOM' && (
                          <div className="permissions-container">
                            <div className="permissions-input">
                              <input
                                type="text"
                                className="form-input"
                                placeholder="Permission name"
                                value={editCustomWritePermission}
                                onChange={(e) => setEditCustomWritePermission(e.target.value)}
                              />
                              <button
                                type="button"
                                className="add-button"
                                onClick={handleAddEditWritePermission}
                              >
                                Add
                              </button>
                            </div>
                            
                            <div className="permissions-badges">
                              {editCollectionWritePermissions.map((perm) => (
                                <div key={perm} className="permission-badge">
                                  {perm}
                                  <button
                                    type="button"
                                    className="remove-permission"
                                    onClick={() => handleRemoveEditWritePermission(perm)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <button
                        type="submit"
                        className="update-button"
                        disabled={loading}
                      >
                        {loading ? 'Updating...' : 'Update Permissions'}
                      </button>
                    </form>
                  </div>
                  
                  {/* Document Stats */}
                  <div className="info-box collection-info">
                    <h3 className="form-title">Collection Information</h3>
                    <p><strong>Total Documents:</strong> {metadataCollection.fields.length}</p>
                    <div className="permission-info">
                      <p><strong>Read Permissions:</strong></p>
                      {renderPermissionBadges(metadataCollection.permissions.read)}
                    </div>
                    <div className="permission-info">
                      <p><strong>Write Permissions:</strong></p>
                      {renderPermissionBadges(metadataCollection.permissions.write)}
                    </div>
                    <p className="permission-note">
                      Users can also access specific documents with <code>docId_READ</code> or <code>docId_WRITE</code> permissions.
                    </p>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="empty-selection">
              Select a collection to view its details and permissions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeveloperControlPanel;